import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase, ActivityLog, AttendanceRecord, Student, Volunteer, School } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import {
  LayoutDashboard, Search, Filter, Users, Calendar, TrendingUp,
  UserPlus, BookOpen, Package, Utensils, Camera, ClipboardCheck,
  ArrowLeftRight, School as SchoolIcon, Activity, Clock, PlusCircle,
} from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, AreaChart, Area,
} from "recharts";

type DashboardTab = "overview" | "attendance" | "activity" | "schools";

const GENDER_COLORS = { female: "#7A2222", male: "#38B6FF" };
const STATUS_COLORS = { present: "#2E7D32", absent: "#D32F2F", sick: "#D97706", leave: "#7C3AED" };

export default function Dashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState<DashboardTab>("overview");

  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<string>("all");

  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [logSearch, setLogSearch] = useState("");

  const [attRecords, setAttRecords] = useState<AttendanceRecord[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [classLookup, setClassLookup] = useState<Record<string, string>>({});

  const [genderFilter, setGenderFilter] = useState("all");
  const [personTypeFilter, setPersonTypeFilter] = useState("all");
  const [classFilter, setClassFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [attSearch, setAttSearch] = useState("");

  const [stats, setStats] = useState({ students: 0, volunteers: 0, attendanceToday: 0, transfers: 0 });

  const [newSchoolName, setNewSchoolName] = useState("");
  const [newSchoolLocation, setNewSchoolLocation] = useState("");
  const [newSchoolDesc, setNewSchoolDesc] = useState("");
  const [addingSchool, setAddingSchool] = useState(false);
  const [schoolError, setSchoolError] = useState("");
  const [schoolSuccess, setSchoolSuccess] = useState("");

  const fetchSchools = useCallback(async () => {
    const { data } = await supabase.from("schools").select("*").order("name");
    if (data) setSchools(data as School[]);
  }, []);

  const fetchLogs = useCallback(async () => {
    let q = supabase.from("activity_logs").select("*").order("created_at", { ascending: false }).limit(200);
    if (selectedSchool !== "all") q = q.eq("school_id", selectedSchool);
    const { data } = await q;
    if (data) setLogs(data as ActivityLog[]);
  }, [selectedSchool]);

  const fetchStats = useCallback(async () => {
    let sQ = supabase.from("students").select("id", { count: "exact", head: true });
    let vQ = supabase.from("volunteers").select("id", { count: "exact", head: true });
    if (selectedSchool !== "all") {
      sQ = sQ.eq("school_id", selectedSchool);
      vQ = vQ.eq("school_id", selectedSchool);
    }
    const [sRes, vRes] = await Promise.all([sQ, vQ]);

    const today = new Date().toISOString().split("T")[0];
    let aQ = supabase.from("attendance").select("id", { count: "exact", head: true }).eq("record_date", today);
    if (selectedSchool !== "all") aQ = aQ.eq("school_id", selectedSchool);
    const aRes = await aQ;

    const tRes = await supabase.from("store_transfers").select("id", { count: "exact", head: true });

    setStats({
      students: sRes.count || 0,
      volunteers: vRes.count || 0,
      attendanceToday: aRes.count || 0,
      transfers: tRes.count || 0,
    });
  }, [selectedSchool]);

  const fetchStudentsAndVolunteers = useCallback(async () => {
    let sQ = supabase.from("students").select("*");
    let vQ = supabase.from("volunteers").select("*");
    if (selectedSchool !== "all") {
      sQ = sQ.eq("school_id", selectedSchool);
      vQ = vQ.eq("school_id", selectedSchool);
    }
    const [sRes, vRes] = await Promise.all([sQ, vQ]);
    if (sRes.data) setStudents(sRes.data as Student[]);
    if (vRes.data) setVolunteers(vRes.data as Volunteer[]);
  }, [selectedSchool]);

  // Students only store class_id (no class_name column), so build a
  // lookup to resolve names for the "Students by Class" chart.
  const fetchClassLookup = useCallback(async () => {
    let q = supabase.from("classes").select("id, name");
    if (selectedSchool !== "all") q = q.eq("school_id", selectedSchool);
    const { data } = await q;
    if (data) {
      const lookup: Record<string, string> = {};
      (data as any[]).forEach((c) => { lookup[c.id] = c.name; });
      setClassLookup(lookup);
    }
  }, [selectedSchool]);

  const fetchAttendance = useCallback(async () => {
    let q = supabase.from("attendance").select("*").order("record_date", { ascending: false }).limit(1000);
    if (selectedSchool !== "all") q = q.eq("school_id", selectedSchool);
    if (genderFilter !== "all") q = q.eq("gender", genderFilter);
    if (personTypeFilter !== "all") q = q.eq("person_type", personTypeFilter);
    if (classFilter !== "all") q = q.eq("class_name", classFilter);
    if (deptFilter !== "all") q = q.eq("department", deptFilter);
    if (dateFrom) q = q.gte("record_date", dateFrom);
    if (dateTo) q = q.lte("record_date", dateTo);
    const { data } = await q;
    if (data) setAttRecords(data as AttendanceRecord[]);
  }, [selectedSchool, genderFilter, personTypeFilter, classFilter, deptFilter, dateFrom, dateTo]);

  useEffect(() => { fetchSchools(); }, [fetchSchools]);
  useEffect(() => {
    fetchLogs();
    fetchStats();
    fetchStudentsAndVolunteers();
    fetchAttendance();
    fetchClassLookup();
  }, [fetchLogs, fetchStats, fetchStudentsAndVolunteers, fetchAttendance, fetchClassLookup]);

  useEffect(() => {
    const channels = [
      supabase.channel("dashboard-logs")
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "activity_logs" }, () => fetchLogs())
        .subscribe(),
      supabase.channel("dashboard-attendance")
        .on("postgres_changes", { event: "*", schema: "public", table: "attendance" }, () => { fetchAttendance(); fetchStats(); })
        .subscribe(),
      supabase.channel("dashboard-students")
        .on("postgres_changes", { event: "*", schema: "public", table: "students" }, () => { fetchStats(); fetchStudentsAndVolunteers(); })
        .subscribe(),
      supabase.channel("dashboard-volunteers")
        .on("postgres_changes", { event: "*", schema: "public", table: "volunteers" }, () => { fetchStats(); fetchStudentsAndVolunteers(); })
        .subscribe(),
      supabase.channel("dashboard-transfers")
        .on("postgres_changes", { event: "*", schema: "public", table: "store_transfers" }, () => fetchStats())
        .subscribe(),
      supabase.channel("dashboard-schools")
        .on("postgres_changes", { event: "*", schema: "public", table: "schools" }, () => fetchSchools())
        .subscribe(),
      supabase.channel("dashboard-classes")
        .on("postgres_changes", { event: "*", schema: "public", table: "classes" }, () => fetchClassLookup())
        .subscribe(),
    ];

    return () => { channels.forEach((c) => supabase.removeChannel(c)); };
  }, [fetchLogs, fetchStats, fetchAttendance, fetchStudentsAndVolunteers, fetchSchools, fetchClassLookup]);

  const classOptions = useMemo(
    () => [...new Set(attRecords.map((r) => r.class_name).filter(Boolean))] as string[],
    [attRecords]
  );
  const deptOptions = useMemo(
    () => [...new Set(attRecords.map((r) => r.department).filter(Boolean))] as string[],
    [attRecords]
  );

  const filteredLogs = logs.filter((l) => {
    if (!logSearch) return true;
    const s = logSearch.toLowerCase();
    return (
      l.action?.toLowerCase().includes(s) ||
      l.user_name?.toLowerCase().includes(s) ||
      l.entity_name?.toLowerCase().includes(s) ||
      l.details?.toLowerCase().includes(s)
    );
  });

  const filteredAtt = attRecords.filter((r) => {
    if (!attSearch) return true;
    return r.person_name?.toLowerCase().includes(attSearch.toLowerCase());
  });

  const presentCount = filteredAtt.filter((r) => r.status === "present").length;
  const absentCount = filteredAtt.filter((r) => r.status === "absent").length;
  const sickCount = filteredAtt.filter((r) => r.status === "sick").length;
  const leaveCount = filteredAtt.filter((r) => r.status === "leave").length;
  const presentPct = filteredAtt.length > 0 ? Math.round((presentCount / filteredAtt.length) * 100) : 0;

  const genderChartData = useMemo(() => {
    const female = students.filter((s) => s.gender === "female").length;
    const male = students.filter((s) => s.gender === "male").length;
    return [
      { name: t("opt_female"), value: female, fill: GENDER_COLORS.female },
      { name: t("opt_male"), value: male, fill: GENDER_COLORS.male },
    ];
  }, [students]);

  const statusChartData = useMemo(() => [
    { name: t("lbl_present"), value: presentCount, fill: STATUS_COLORS.present },
    { name: t("lbl_absent"), value: absentCount, fill: STATUS_COLORS.absent },
    { name: "Sick", value: sickCount, fill: STATUS_COLORS.sick },
    { name: "Leave", value: leaveCount, fill: STATUS_COLORS.leave },
  ], [presentCount, absentCount, sickCount, leaveCount]);

  const classChartData = useMemo(() => {
    const counts: Record<string, number> = {};
    students.forEach((s) => {
      const cid = (s as any).class_id as string | null;
      const key = (cid && classLookup[cid]) || "Unassigned";
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 12);
  }, [students, classLookup]);

  const departmentChartData = useMemo(() => {
    const counts: Record<string, number> = {};
    volunteers.forEach((v) => {
      const key = v.department || "Unassigned";
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [volunteers]);

  const schoolChartData = useMemo(() => {
    if (selectedSchool !== "all") return [];
    return schools.map((sch) => ({
      name: sch.name,
      students: students.filter((s) => (s as any).school_id === sch.id).length,
      volunteers: volunteers.filter((v) => (v as any).school_id === sch.id).length,
    }));
  }, [schools, students, volunteers, selectedSchool]);

  const attendanceTrendData = useMemo(() => {
    const byDate: Record<string, { present: number; absent: number; sick: number; leave: number }> = {};
    attRecords.forEach((r) => {
      if (!byDate[r.record_date]) byDate[r.record_date] = { present: 0, absent: 0, sick: 0, leave: 0 };
      const key = r.status as "present" | "absent" | "sick" | "leave";
      if (byDate[r.record_date][key] !== undefined) byDate[r.record_date][key]++;
    });
    return Object.entries(byDate)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-14)
      .map(([date, counts]) => ({ date: date.slice(5), ...counts }));
  }, [attRecords]);

  const handleAddSchool = async (e: React.FormEvent) => {
    e.preventDefault();
    setSchoolError("");
    setSchoolSuccess("");
    if (!newSchoolName.trim()) {
      setSchoolError("School name is required.");
      return;
    }
    setAddingSchool(true);
    const { error } = await supabase.from("schools").insert({
      name: newSchoolName.trim(),
      location: newSchoolLocation.trim() || null,
      description: newSchoolDesc.trim() || null,
    });
    setAddingSchool(false);
    if (error) {
      setSchoolError(error.message);
      return;
    }
    setSchoolSuccess(`"${newSchoolName}" added. It's now selectable in Student Registration.`);
    setNewSchoolName("");
    setNewSchoolLocation("");
    setNewSchoolDesc("");
    await fetchSchools();
  };

  const actionIcon = (action: string) => {
    const a = action.toLowerCase();
    if (a.includes("student")) return <UserPlus className="w-4 h-4 text-[#38B6FF]" />;
    if (a.includes("volunteer") || a.includes("worker")) return <Users className="w-4 h-4 text-[#1C3988]" />;
    if (a.includes("class")) return <BookOpen className="w-4 h-4 text-[#7A2222]" />;
    if (a.includes("attendance")) return <ClipboardCheck className="w-4 h-4 text-[#2E7D32]" />;
    if (a.includes("store") || a.includes("material")) return <Package className="w-4 h-4 text-[#D97706]" />;
    if (a.includes("food")) return <Utensils className="w-4 h-4 text-[#D97706]" />;
    if (a.includes("media")) return <Camera className="w-4 h-4 text-[#7A2222]" />;
    if (a.includes("transfer")) return <ArrowLeftRight className="w-4 h-4 text-[#1C3988]" />;
    if (a.includes("school")) return <SchoolIcon className="w-4 h-4 text-[#38B6FF]" />;
    return <Activity className="w-4 h-4 text-gray-400" />;
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t("dash_just_now");
    if (mins < 60) return `${mins}${t("dash_min_ago")}`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}${t("dash_hr_ago")}`;
    const days = Math.floor(hrs / 24);
    return `${days}${t("dash_day_ago")}`;
  };

  return (
    <div className="animate-fadeIn">
      <div className="card mb-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold">
            <LayoutDashboard className="w-6 h-6" /> {t("dash_title")}
          </h2>
          <div className="flex items-center gap-2">
            <SchoolIcon className="w-5 h-5 text-[#38B6FF]" />
            <select
              className="form-input min-w-[200px]"
              value={selectedSchool}
              onChange={(e) => setSelectedSchool(e.target.value)}
            >
              <option value="all">{t("lbl_all_schools")}</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex gap-2.5 mb-6 flex-wrap">
        <button className={tab === "overview" ? "btn-primary" : "btn-cyan"} onClick={() => setTab("overview")}>
          <TrendingUp className="w-5 h-5" /> {t("dash_tab_overview")}
        </button>
        <button className={tab === "attendance" ? "btn-primary" : "btn-cyan"} onClick={() => setTab("attendance")}>
          <ClipboardCheck className="w-5 h-5" /> {t("dash_tab_attendance")}
        </button>
        <button className={tab === "activity" ? "btn-primary" : "btn-cyan"} onClick={() => setTab("activity")}>
          <Activity className="w-5 h-5" /> {t("dash_tab_activity")}
        </button>
        <button className={tab === "schools" ? "btn-primary" : "btn-cyan"} onClick={() => setTab("schools")}>
          <SchoolIcon className="w-5 h-5" /> {t("schools_title")}
        </button>
      </div>

      {tab === "overview" && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="card p-5 text-center">
              <div className="w-12 h-12 bg-[#38B6FF]/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                <UserPlus className="w-6 h-6 text-[#38B6FF]" />
              </div>
              <p className="text-3xl font-extrabold text-[#1C3988]">{stats.students}</p>
              <p className="text-sm text-gray-500 mt-1">{t("dash_total_students")}</p>
            </div>
            <div className="card p-5 text-center">
              <div className="w-12 h-12 bg-[#1C3988]/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-[#1C3988]" />
              </div>
              <p className="text-3xl font-extrabold text-[#1C3988]">{stats.volunteers}</p>
              <p className="text-sm text-gray-500 mt-1">{t("dash_total_volunteers")}</p>
            </div>
            <div className="card p-5 text-center">
              <div className="w-12 h-12 bg-[#2E7D32]/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                <ClipboardCheck className="w-6 h-6 text-[#2E7D32]" />
              </div>
              <p className="text-3xl font-extrabold text-[#1C3988]">{stats.attendanceToday}</p>
              <p className="text-sm text-gray-500 mt-1">{t("dash_attendance_today")}</p>
            </div>
            <div className="card p-5 text-center">
              <div className="w-12 h-12 bg-[#D97706]/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                <ArrowLeftRight className="w-6 h-6 text-[#D97706]" />
              </div>
              <p className="text-3xl font-extrabold text-[#1C3988]">{stats.transfers}</p>
              <p className="text-sm text-gray-500 mt-1">{t("dash_transfers")}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            <div className="card">
              <h3 className="text-[#1C3988] font-bold mb-3">Students by Gender</h3>
              {genderChartData.every((d) => d.value === 0) ? (
                <p className="text-gray-400 italic text-sm py-8 text-center">No student data yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={genderChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                      {genderChartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="card">
              <h3 className="text-[#1C3988] font-bold mb-3">Attendance Status (filtered)</h3>
              {statusChartData.every((d) => d.value === 0) ? (
                <p className="text-gray-400 italic text-sm py-8 text-center">No attendance data yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={statusChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                      {statusChartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="card">
              <h3 className="text-[#1C3988] font-bold mb-3">Students by Class</h3>
              {classChartData.length === 0 ? (
                <p className="text-gray-400 italic text-sm py-8 text-center">No class assignments yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={classChartData} margin={{ left: 0, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#1C3988" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="card">
              <h3 className="text-[#1C3988] font-bold mb-3">Volunteers by Department</h3>
              {departmentChartData.length === 0 ? (
                <p className="text-gray-400 italic text-sm py-8 text-center">No volunteer data yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={departmentChartData} margin={{ left: 0, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={70} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#38B6FF" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {selectedSchool === "all" && schoolChartData.length > 0 && (
              <div className="card lg:col-span-2">
                <h3 className="text-[#1C3988] font-bold mb-3">Students & Volunteers by School</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={schoolChartData} margin={{ left: 0, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="students" name={t("dash_total_students")} fill="#1C3988" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="volunteers" name={t("dash_total_volunteers")} fill="#38B6FF" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="card lg:col-span-2">
              <h3 className="text-[#1C3988] font-bold mb-3">Attendance Trend — Last 14 Recorded Days</h3>
              {attendanceTrendData.length === 0 ? (
                <p className="text-gray-400 italic text-sm py-8 text-center">No attendance recorded yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={attendanceTrendData} margin={{ left: 0, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="present" stackId="1" stroke={STATUS_COLORS.present} fill={STATUS_COLORS.present} fillOpacity={0.6} name={t("lbl_present")} />
                    <Area type="monotone" dataKey="absent" stackId="1" stroke={STATUS_COLORS.absent} fill={STATUS_COLORS.absent} fillOpacity={0.6} name={t("lbl_absent")} />
                    <Area type="monotone" dataKey="sick" stackId="1" stroke={STATUS_COLORS.sick} fill={STATUS_COLORS.sick} fillOpacity={0.6} name="Sick" />
                    <Area type="monotone" dataKey="leave" stackId="1" stroke={STATUS_COLORS.leave} fill={STATUS_COLORS.leave} fillOpacity={0.6} name="Leave" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="card">
            <h3 className="text-[#1C3988] flex items-center gap-2 font-bold mb-4">
              <Clock className="w-5 h-5" /> {t("dash_recent_activity")}
            </h3>
            {filteredLogs.length === 0 ? (
              <p className="text-gray-400 italic">{t("dash_no_activity")}</p>
            ) : (
              <div className="space-y-2">
                {filteredLogs.slice(0, 15).map((log) => (
                  <div key={log.id} className="flex items-start gap-3 p-3 bg-[#F8FAFC] rounded-lg border border-gray-100">
                    <div className="mt-0.5">{actionIcon(log.action)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">
                        <strong className="text-[#1C3988]">{log.user_name || t("dash_unknown")}</strong>{" "}
                        <span className="text-gray-600">{log.action}</span>
                        {log.entity_name && <span className="text-gray-800 font-semibold"> {log.entity_name}</span>}
                      </p>
                      {log.details && <p className="text-xs text-gray-400 mt-0.5">{log.details}</p>}
                      <p className="text-xs text-gray-400 mt-0.5">{timeAgo(log.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "attendance" && (
        <div className="card">
          <h3 className="text-[#1C3988] flex items-center gap-2 font-bold mb-4">
            <ClipboardCheck className="w-5 h-5" /> {t("dash_tab_attendance")}
          </h3>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <div className="bg-[#F1F5F9] p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-[#1C3988]">{filteredAtt.length}</p>
              <p className="text-xs text-gray-500">{t("lbl_total_records")}</p>
            </div>
            <div className="bg-[#E8F5E9] p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-[#2E7D32]">{presentCount}</p>
              <p className="text-xs text-gray-500">{t("lbl_present")}</p>
            </div>
            <div className="bg-[#FFEBEE] p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-[#D32F2F]">{absentCount}</p>
              <p className="text-xs text-gray-500">{t("lbl_absent")}</p>
            </div>
            <div className="bg-amber-50 p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-[#D97706]">{sickCount}</p>
              <p className="text-xs text-gray-500">Sick</p>
            </div>
            <div className="bg-[#FFF3E0] p-3 rounded-lg text-center">
              <p className="text-2xl font-bold text-[#D97706]">{presentPct}%</p>
              <p className="text-xs text-gray-500">{t("lbl_present_rate")}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="form-group">
              <label className="form-label">{t("lbl_person_type")}</label>
              <select className="form-input" value={personTypeFilter} onChange={(e) => setPersonTypeFilter(e.target.value)}>
                <option value="all">{t("lbl_all")}</option>
                <option value="student">{t("opt_students")}</option>
                <option value="volunteer">{t("opt_volunteers")}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_gender")}</label>
              <select className="form-input" value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)}>
                <option value="all">{t("lbl_all")}</option>
                <option value="female">{t("opt_female")}</option>
                <option value="male">{t("opt_male")}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_class")}</label>
              <select className="form-input" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
                <option value="all">{t("lbl_all_classes")}</option>
                {classOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_department")}</label>
              <select className="form-input" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
                <option value="all">{t("lbl_all_departments")}</option>
                {deptOptions.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_date_from")}</label>
              <input className="form-input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">{t("lbl_date_to")}</label>
              <input className="form-input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div className="form-group md:col-span-2">
              <label className="form-label">{t("lbl_search_name")}</label>
              <div className="relative">
                <Search className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                <input
                  className="form-input pl-10"
                  type="text"
                  placeholder={t("ph_search_name")}
                  value={attSearch}
                  onChange={(e) => setAttSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          <button
            className="btn-cyan btn-sm mb-4"
            onClick={() => {
              setGenderFilter("all"); setPersonTypeFilter("all"); setClassFilter("all");
              setDeptFilter("all"); setDateFrom(""); setDateTo(""); setAttSearch("");
            }}
          >
            <Filter className="w-4 h-4" /> {t("btn_clear_filters")}
          </button>

          {filteredAtt.length === 0 ? (
            <p className="text-gray-400 italic">{t("dash_no_attendance")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-200 text-left text-[#1C3988]">
                    <th className="py-2 px-2">{t("lbl_date")}</th>
                    <th className="py-2 px-2">{t("lbl_name")}</th>
                    <th className="py-2 px-2">{t("lbl_type")}</th>
                    <th className="py-2 px-2">{t("lbl_gender")}</th>
                    <th className="py-2 px-2">{t("lbl_class_dept")}</th>
                    <th className="py-2 px-2">{t("lbl_status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAtt.map((r) => (
                    <tr key={r.id} className="border-b border-gray-100 hover:bg-[#F8FAFC]">
                      <td className="py-2 px-2 text-gray-500 whitespace-nowrap">{r.record_date}</td>
                      <td className="py-2 px-2 font-semibold text-[#1C3988]">{r.person_name}</td>
                      <td className="py-2 px-2 capitalize">{r.person_type}</td>
                      <td className="py-2 px-2 capitalize">{r.gender || "-"}</td>
                      <td className="py-2 px-2">{r.class_name || r.department || "-"}</td>
                      <td className="py-2 px-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          r.status === "present" ? "bg-[#E8F5E9] text-[#2E7D32]"
                          : r.status === "absent" ? "bg-[#FFEBEE] text-[#D32F2F]"
                          : r.status === "sick" ? "bg-amber-50 text-[#D97706]"
                          : "bg-purple-50 text-[#7C3AED]"
                        }`}>
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "activity" && (
        <div className="card">
          <h3 className="text-[#1C3988] flex items-center gap-2 font-bold mb-4">
            <Activity className="w-5 h-5" /> {t("dash_full_activity_log")}
          </h3>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
            <input
              className="form-input pl-10"
              type="text"
              placeholder={t("ph_search_activity")}
              value={logSearch}
              onChange={(e) => setLogSearch(e.target.value)}
            />
          </div>
          {filteredLogs.length === 0 ? (
            <p className="text-gray-400 italic">{t("dash_no_logs")}</p>
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredLogs.map((log) => (
                <div key={log.id} className="flex items-start gap-3 p-3 bg-[#F8FAFC] rounded-lg border border-gray-100">
                  <div className="mt-0.5">{actionIcon(log.action)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <strong className="text-[#1C3988]">{log.user_name || t("dash_unknown")}</strong>{" "}
                      <span className="text-gray-600">{log.action}</span>
                      {log.entity_name && <span className="text-gray-800 font-semibold"> {log.entity_name}</span>}
                    </p>
                    {log.details && <p className="text-xs text-gray-400 mt-0.5">{log.details}</p>}
                    <div className="flex items-center gap-3 mt-1">
                      <p className="text-xs text-gray-400">{new Date(log.created_at).toLocaleString()}</p>
                      {log.entity_type && (
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full text-gray-500">{log.entity_type}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "schools" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="card">
            <h3 className="text-[#1C3988] flex items-center gap-2 font-bold mb-4">
              <PlusCircle className="w-5 h-5" /> {t("btn_add_school")}
            </h3>
            <form onSubmit={handleAddSchool} className="space-y-4">
              <div className="form-group">
                <label className="form-label">{t("lbl_school_name")}</label>
                <input
                  className="form-input"
                  type="text"
                  value={newSchoolName}
                  onChange={(e) => setNewSchoolName(e.target.value)}
                  placeholder={t("ph_school_name")}
                  required
                  disabled={addingSchool}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t("lbl_location")}</label>
                <input
                  className="form-input"
                  type="text"
                  value={newSchoolLocation}
                  onChange={(e) => setNewSchoolLocation(e.target.value)}
                  placeholder={t("ph_location")}
                  disabled={addingSchool}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t("lbl_school_desc")}</label>
                <input
                  className="form-input"
                  type="text"
                  value={newSchoolDesc}
                  onChange={(e) => setNewSchoolDesc(e.target.value)}
                  placeholder={t("ph_school_desc")}
                  disabled={addingSchool}
                />
              </div>
              {schoolError && <div className="text-[#D32F2F] bg-red-50 p-3 rounded-md text-sm">{schoolError}</div>}
              {schoolSuccess && <div className="text-[#2E7D32] bg-green-50 p-3 rounded-md text-sm">{schoolSuccess}</div>}
              <button type="submit" disabled={addingSchool} className="btn-cyan">
                <PlusCircle className="w-5 h-5" /> {addingSchool ? t("btn_saving") : t("btn_add_school")}
              </button>
            </form>
          </div>

          <div className="card">
            <h3 className="text-[#1C3988] flex items-center gap-2 font-bold mb-4">
              <SchoolIcon className="w-5 h-5" /> {t("schools_all")} ({schools.length})
            </h3>
            {schools.length === 0 ? (
              <p className="text-gray-400 italic">{t("schools_none")}</p>
            ) : (
              <div className="space-y-2">
                {schools.map((s) => (
                  <div key={s.id} className="attendance-row">
                    <div>
                      <strong className="text-[#1C3988]">{s.name}</strong>
                      {s.location && <span className="text-sm text-gray-400 ml-2">| {s.location}</span>}
                      {s.description && <p className="text-xs text-gray-400 mt-0.5">{s.description}</p>}
                    </div>
                    <span className="text-xs bg-[#1C3988] text-white px-2 py-1 rounded-full font-semibold">
                      {students.filter((st) => (st as any).school_id === s.id).length} {t("opt_students").toLowerCase()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
